import { LocateIcon, MapPinIcon, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { geocodeCity, reverseGeocode } from '@/lib/geocode'

export function LocationSetupCard({
  onSave,
  onDismiss,
}: {
  onSave: (city: string, country: string, lat: number, lon: number) => void
  onDismiss: () => void
}) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<'prompt' | 'manual' | 'locating'>('prompt')
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)

  async function requestGeolocation() {
    if (!('geolocation' in navigator)) {
      setMode('manual')
      return
    }
    setMode('locating')
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const result = await reverseGeocode(pos.coords.latitude, pos.coords.longitude)
        if (result) {
          onSave(result.city, result.country, result.lat, result.lon)
        } else {
          setValue('')
          setMode('manual')
        }
      },
      () => {
        setValue('')
        setMode('manual')
      },
      { timeout: 10000 },
    )
  }

  async function handleManualSave() {
    const trimmed = value.trim()
    if (!trimmed) return
    setSaving(true)
    setError(false)
    const result = await geocodeCity(trimmed)
    setSaving(false)
    if (!result) {
      setError(true)
      return
    }
    onSave(result.city, result.country, result.lat, result.lon)
  }

  return (
    <div className="mb-3 flex items-start gap-3 rounded-xl border border-border bg-card px-4 py-3.5">
      <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/60" strokeWidth={1.5} />
      <div className="min-w-0 flex-1">
        {mode === 'prompt' && (
          <div className="flex flex-col items-start gap-2">
            <p className="text-sm text-foreground/80">{t('location.setupPrompt')}</p>
            <button
              type="button"
              onClick={() => void requestGeolocation()}
              className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary-text hover:bg-primary/20 transition-colors"
            >
              <LocateIcon className="h-3 w-3" strokeWidth={2.5} />
              {t('location.useMyLocation')}
            </button>
          </div>
        )}
        {mode === 'locating' && (
          <p className="text-sm text-muted-foreground/60">{t('location.detecting')}</p>
        )}
        {mode === 'manual' && (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-foreground/80">{t('location.enterCity')}</p>
            <div className="flex items-center gap-2">
              <input
                autoFocus
                type="text"
                value={value}
                onChange={(e) => {
                  setValue(e.target.value)
                  setError(false)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleManualSave()
                }}
                placeholder={t('location.cityPlaceholder')}
                className="flex-1 rounded border border-border bg-background px-2 py-1.5 text-sm text-foreground outline-none focus:border-primary/50"
              />
              <button
                type="button"
                onClick={() => void handleManualSave()}
                disabled={saving || !value.trim()}
                className="cursor-pointer rounded bg-primary px-3 py-1.5 text-xs font-medium text-white dark:text-[#0a0a0a] disabled:opacity-40"
              >
                {saving ? '…' : t('common.save')}
              </button>
            </div>
            {error && <p className="text-xs text-destructive">{t('settings.location.notFound')}</p>}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="mt-0.5 flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded text-muted-foreground/40 transition-colors hover:text-muted-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
